import { useState, useEffect, useCallback } from 'react';
import { UserProfile } from './interfaces';
import { parseNestedProfile } from './profileUtils';
import API_BASE_URL, { buildApiHeaders } from './config';

const ANONYMOUS_PROFILE_STORAGE_KEY = 'anonymousUserProfile';

// Default user profile template - only include essential sections
export const createDefaultUserProfile = () => ({
    general: {
      gender: "Male",
      handedness: "Right",
      handicap: null,
      playing_frequency: "",
      skill_level: "",
      preferred_brands: "",
      // Measurement fields
      height_inches: null,
      wrist_to_floor_inches: null,
      swing_speed: null,
      attack_angle: null,
      launch_angle: null,
      spin_rate: null,
      swinging_type: null,
      tempo: null,
      course_conditions: null
    }
  });

export const useUserProfile = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editableProfile, setEditableProfile] = useState<UserProfile | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(false);

  const ensureEditableProfile = useCallback(() => {
    if (editableProfile) {
      return editableProfile;
    }

    const fallbackProfile = createDefaultUserProfile() as UserProfile;
    setEditableProfile(fallbackProfile);
    setUserProfile(fallbackProfile);
    setIsNewProfile(true);
    return fallbackProfile;
  }, [editableProfile]);

  const loadUserProfile = useCallback(async () => {
    setProfileLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (token) {
        setIsLoggedIn(true);
        const payload = JSON.parse(atob(token.split('.')[1]));
        const username = payload.username; // Extract username from token

        const data = await fetchUserProfile(token);
        if (data && data.profile) {
          const parsed = parseNestedProfile(data.profile);
          parsed.username = username; // Add username to profile object
          setUserProfile(parsed);
          setEditableProfile(JSON.parse(JSON.stringify(parsed)));
          setIsNewProfile(false);
        } else {
          // New logged-in user with no profile yet, create one from localStorage or a default one.
          const localProfileData = localStorage.getItem(ANONYMOUS_PROFILE_STORAGE_KEY);
          const newProfile = localProfileData ? JSON.parse(localProfileData) : createDefaultUserProfile();
          newProfile.username = username; // Add username to new profile
          setUserProfile(newProfile);
          setEditableProfile(newProfile);
          setIsNewProfile(!localProfileData);
        }
      } else {
        setIsLoggedIn(false);
        const localProfileData = localStorage.getItem(ANONYMOUS_PROFILE_STORAGE_KEY);
        if (localProfileData) {
          const localProfile = JSON.parse(localProfileData);
          setUserProfile(localProfile);
          setEditableProfile(JSON.parse(JSON.stringify(localProfile)));
          setIsNewProfile(false);
        } else {
          const fallbackProfile = createDefaultUserProfile() as UserProfile;
          setUserProfile(fallbackProfile);
          setEditableProfile(JSON.parse(JSON.stringify(fallbackProfile)));
          setIsNewProfile(true);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? `Failed to load user profile: ${err.message}` : 'An unknown error occurred while fetching the user profile.');
      setUserProfile(null);
      setEditableProfile(null);
      setIsNewProfile(false);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!editableProfile) return;

    const { name, value } = e.target;
    
    // Handle club deletion
    if (value === 'DELETE_CLUB') {
      setEditableProfile(prev => {
        if (!prev) return null;
        const newProfile = JSON.parse(JSON.stringify(prev));
        delete (newProfile as any)[name];
        return newProfile;
      });
      return;
    }

    const [section, field] = name.split('.');

    const isNumericField = ['height_inches', 'wrist_to_floor_inches', 'handicap', 'distance'].includes(field);
    const processedValue = isNumericField && value !== '' ? Number(value) : value === '' ? null : value;

    setEditableProfile(prev => {
        if (!prev) return null;
        
        const newProfile = JSON.parse(JSON.stringify(prev));
        const sectionKey = section as keyof UserProfile;

        // Always create section if it doesn't exist (for adding clubs or editing any field)
        if (!newProfile[sectionKey]) {
            (newProfile as any)[sectionKey] = {};
        }

        // Set the field value (including null for empty fields)
        (newProfile[sectionKey] as any)[field] = processedValue;

        return newProfile;
    });
  };

  const saveProfile = async (profileToSave: Partial<UserProfile>) => {
    // console.info('Saving profile:', profileToSave);
    setProfileLoading(true);
    setError(null);

    const syncTopLevelDistanceFields = (profile: Partial<UserProfile>): Partial<UserProfile> => {
        const syncedProfile = JSON.parse(JSON.stringify(profile));

        const driverDistance = syncedProfile.Driver?.distance ?? syncedProfile.driverDistance;
        if (driverDistance !== undefined && driverDistance !== null) {
            syncedProfile.driverDistance = Number(driverDistance);
            syncedProfile.Driver = {
                ...(syncedProfile.Driver ?? { model: null, shaft: null, distance: null, notes: null }),
                distance: Number(driverDistance),
            };
        }

        const sevenIronDistance = syncedProfile['7-Iron']?.distance ?? syncedProfile.sevenIronDistance;
        if (sevenIronDistance !== undefined && sevenIronDistance !== null) {
            syncedProfile.sevenIronDistance = Number(sevenIronDistance);
            syncedProfile['7-Iron'] = {
                ...(syncedProfile['7-Iron'] ?? { model: null, shaft: null, distance: null, notes: null }),
                distance: Number(sevenIronDistance),
            };
        }

        const pitchingWedgeDistance = syncedProfile['Pitching Wedge']?.distance ?? syncedProfile.pitchingWedgeDistance;
        if (pitchingWedgeDistance !== undefined && pitchingWedgeDistance !== null) {
            syncedProfile.pitchingWedgeDistance = Number(pitchingWedgeDistance);
            syncedProfile['Pitching Wedge'] = {
                ...(syncedProfile['Pitching Wedge'] ?? { model: null, shaft: null, distance: null, notes: null }),
                distance: Number(pitchingWedgeDistance),
            };
        }

        return syncedProfile;
    };

    // Helper function to remove empty/null fields and empty sections before saving
    const cleanProfile = (profile: Partial<UserProfile>): Partial<UserProfile> => {
        const cleanedProfile = JSON.parse(JSON.stringify(profile)); // Deep copy

        Object.keys(cleanedProfile).forEach(sectionKey => {
            const section = (cleanedProfile as any)[sectionKey];
            if (typeof section === 'object' && section !== null) {
                // Remove empty/null fields within the section
                Object.keys(section).forEach(field => {
                    if (
                      section[field] === null ||
                      section[field] === '' ||
                      (typeof section[field] === 'string' && section[field].trim() === '')
                    ) {
                        delete section[field];
                    }
                });
                
                // Remove the entire section if it's empty or only contains empty values
                const remainingKeys = Object.keys(section);
                if (remainingKeys.length === 0) {
                    delete (cleanedProfile as any)[sectionKey];
                }
            }
        });
        return cleanedProfile;
    };

    try {
        const cleanedProfileToSave = cleanProfile(syncTopLevelDistanceFields(profileToSave));
        const token = localStorage.getItem('token');

        if (token) {
            let email: string;
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                email = payload.sub;
                if (!email) {
                    throw new Error("Email (sub) not found in token payload.");
                }
            } catch (e) {
                throw new Error("Invalid authentication token. Please log in again.");
            }
            
            await updateUserProfile(token, email, cleanedProfileToSave as UserProfile);
        } 

        localStorage.setItem(ANONYMOUS_PROFILE_STORAGE_KEY, JSON.stringify(cleanedProfileToSave));

        await loadUserProfile(); // Reload profile after saving
    } catch (err) {
        setError(err instanceof Error ? `Failed to save profile: ${err.message}` : 'An unknown error occurred');
        throw err; // re-throw to be caught in component
    } finally {
        setProfileLoading(false);
    }
  };
  
  const handleProfileSave = async () => {
      if (editableProfile) {
          await saveProfile(editableProfile);
      }
  };

  const createProfile = async (profileData: Partial<UserProfile>) => {
      await saveProfile(profileData);
  }

  return {
    userProfile,
    profileLoading,
    error,
    editableProfile,
    isLoggedIn,
    isNewProfile,
    handleProfileChange,
    handleProfileSave,
    createProfile,
    saveProfile,
    setError,
    loadUserProfile,
    ensureEditableProfile,
  };
};

export const fetchUserProfile = async (token: string) => {
  const response = await fetch(`${API_BASE_URL}/get_user_profile`, {
    headers: buildApiHeaders({
      'Authorization': `Bearer ${token}`,
    })
  });

  if (response.status === 404) {
    return null;
  } else if (!response.ok) {
    throw new Error(`Failed to load user profile: ${response.statusText}`);
  }
  const data = await response.json();
  return data;
};

export const updateUserProfile = async (token: string, email: string, profile: UserProfile) => {
    const payload = {
        email: email,
        org: 'neutralfit',
        profile: JSON.stringify(profile),
    };

    const response = await fetch(`${API_BASE_URL}/update_user_profile`, {
        method: 'POST',
        headers: buildApiHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save your profile: ${errorText}`);
    }
    return await response.json();
};

export const fetchRecommendations = async (token: string | null, userProfile: UserProfile, club?: string, brands?: string, budget?: string, condition?: 'new' | 'used', lookupQuery?: string) => {
    const headers: HeadersInit = buildApiHeaders({
        'Content-Type': 'application/json',
    });

    if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    let url = `${API_BASE_URL}/golfer_flash_api`;
    let body: any = {
        user_profile: JSON.stringify(userProfile),
        club: club,
        brands: brands,
        budget: budget,
        condition: condition
    };

    if (lookupQuery) {
        url = `${API_BASE_URL}/golfer_club_lookup_api`;
        body = {
            user_profile: JSON.stringify(userProfile),
            user_query: lookupQuery,
            brands: brands,
            budget: budget,
            condition: condition
        };
    }

    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rawData = await response.text();
    const cleanedData = rawData.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(cleanedData);
};

export const analyzeUserProfile = async (userProfile: UserProfile) => {
    const headers: HeadersInit = buildApiHeaders({
        'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/golfer_analysis_api`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
            user_profile: JSON.stringify(userProfile),
        }),
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rawData = await response.text();
    let cleanedData = rawData.replace(/```json/g, '').replace(/```/g, '').trim();
    
    try {
        // Parse as standard JSON
        let parsed = JSON.parse(cleanedData);
        
        // Check if the result is still a string (double-encoded)
        if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
        }
        
        return parsed;
        
    } catch (error) {
        console.error('Failed to parse analysis response:', cleanedData);
        console.error('Original raw data:', rawData);
        throw new Error('Invalid response format from analysis API');
    }
};
